// The brand-health event vocabulary — event types, routes, outcomes, severities — shared verbatim
// by the node emitters/detectors and the react tracking-health surfaces. Dumb constants (no
// imports): one home, so the two sides structurally cannot drift.

const BRAND_HEALTH_EVENT_TYPES = {
  CLICK: 'click',
  CLICK_CONFIRMATION: 'click_confirmation',
  ORDER_SEEN: 'order_seen',
  ORDER_TRACKED: 'order_tracked',
  ORDER_UPDATE_SEEN: 'order_update_seen',
  ORDER_UPDATE_TRACKED: 'order_update_tracked',
  ORDER_CANCEL_SEEN: 'order_cancel_seen',
  ORDER_CANCEL_TRACKED: 'order_cancel_tracked',
  SHOPIFY_API_CALL: 'shopify_api_call'
};

const BRAND_HEALTH_ROUTES = {
  REDIRECT_CLICK: 'redirect_click',
  CONFIRM_CLICK: 'confirm_click',
  ORDER_CONFIRMATION: 'order_confirmation',
  SHOPIFY_ORDER_CREATED: 'shopify_order_created',
  AFFILIATES_UPDATE: 'affiliates_update',
  SHOPIFY_ORDER_UPDATED: 'shopify_order_updated',
  SHOPIFY_ORDER_FULFILLED: 'shopify_order_fulfilled',
  SHOPIFY_NIGHTLY_SYNC: 'shopify_nightly_sync',
  AFFILIATES_CANCEL: 'affiliates_cancel',
  SHOPIFY_ORDER_CANCELLED: 'shopify_order_cancelled',
  PARTNERS_CANCEL: 'partners_cancel',
  WEBHOOK_ZENOTI: 'webhook_zenoti',
  WEBHOOK_MIVA: 'webhook_miva',
  WEBHOOK_BIGCOMMERCE: 'webhook_bigcommerce'
};

const BRAND_HEALTH_CLICK_OUTCOMES = {
  FRESH: 'fresh',
  DUPLICATE_CLICK: 'duplicate_click',
  BOT_BLOCKED: 'bot_blocked',
  HANDLER_ERROR: 'handler_error'
};

const BRAND_HEALTH_ORDER_OUTCOMES = {
  TRACKED: 'tracked',
  DUPLICATE_ORDER: 'duplicate_order',
  NO_ATTRIBUTION: 'no_attribution',
  UNREGISTERED_CODE: 'unregistered_code',
  BLACKLISTED_CODE: 'blacklisted_code',
  UNREGISTERED_PROPERTY: 'unregistered_property',
  SHOPIFY_HANDOFF: 'shopify_handoff',
  INVALID_ORDER_AMOUNT: 'invalid_order_amount',
  INVALID_ORDER_ID: 'invalid_order_id',
  CLICK_NOT_FOUND: 'click_not_found',
  MALFORMED_CLICK_ID: 'malformed_click_id',
  UNRESOLVED_DOMAIN: 'unresolved_domain',
  ORDER_NOT_FOUND: 'order_not_found',
  ALREADY_CANCELLED: 'already_cancelled',
  INVALID_PARAMS: 'invalid_params',
  BANNED_USER: 'banned_user',
  POS_ORDER: 'pos_order',
  DRAFT_ORDER: 'draft_order',
  ZERO_AMOUNT_ORDER: 'zero_amount_order',
  MULTIPLE_OFFER_CODES: 'multiple_offer_codes',
  MISCONFIGURED_PIXEL: 'misconfigured_pixel',
  INSERT_FAILED: 'insert_failed',
  HANDLER_ERROR: 'handler_error'
};

// Every outbound Shopify Admin API call, emitted from createShopifySession's instrumented client.
// The route field carries the operation class (order_creation, codes, products...), so the buckets
// answer "how many gifting-order calls failed" — not just "how many calls failed".
const BRAND_HEALTH_SHOPIFY_API_OUTCOMES = {
  SUCCESS: 'success',
  AUTH_ERROR: 'auth_error',
  RATE_LIMITED: 'rate_limited',
  SERVER_ERROR: 'server_error',
  CLIENT_ERROR: 'client_error',
  SESSION_INIT_FAILED: 'session_init_failed'
};

const BRAND_HEALTH_OUTCOME_KEY_PREFIX = {
  [BRAND_HEALTH_EVENT_TYPES.CLICK]: 'clickOutcome',
  [BRAND_HEALTH_EVENT_TYPES.CLICK_CONFIRMATION]: 'clickConfirmationOutcome',
  [BRAND_HEALTH_EVENT_TYPES.ORDER_SEEN]: 'orderSeenOutcome',
  [BRAND_HEALTH_EVENT_TYPES.ORDER_TRACKED]: 'orderTrackedOutcome',
  [BRAND_HEALTH_EVENT_TYPES.ORDER_UPDATE_SEEN]: 'orderUpdateSeenOutcome',
  [BRAND_HEALTH_EVENT_TYPES.ORDER_UPDATE_TRACKED]: 'orderUpdateTrackedOutcome',
  [BRAND_HEALTH_EVENT_TYPES.ORDER_CANCEL_SEEN]: 'orderCancelSeenOutcome',
  [BRAND_HEALTH_EVENT_TYPES.ORDER_CANCEL_TRACKED]: 'orderCancelTrackedOutcome',
  [BRAND_HEALTH_EVENT_TYPES.SHOPIFY_API_CALL]: 'shopifyApiOutcome'
};

// Severity is a product judgment: error = the brand (or we) should fix it; neutral = received and
// handled, nothing to act on. Drives the tracking-health page bands and the alert detectors.
const BRAND_HEALTH_OUTCOME_SEVERITY = {
  [BRAND_HEALTH_ORDER_OUTCOMES.TRACKED]: 'success',
  [BRAND_HEALTH_CLICK_OUTCOMES.FRESH]: 'success',
  [BRAND_HEALTH_ORDER_OUTCOMES.INVALID_ORDER_AMOUNT]: 'error',
  [BRAND_HEALTH_ORDER_OUTCOMES.INVALID_ORDER_ID]: 'error',
  [BRAND_HEALTH_ORDER_OUTCOMES.MALFORMED_CLICK_ID]: 'error',
  [BRAND_HEALTH_ORDER_OUTCOMES.INVALID_PARAMS]: 'error',
  // Mirror-only: the pixel path never queues these (pageview flood), so this error can't reach
  // buckets or the error-rate detectors — it grades the liveData inspector honestly.
  [BRAND_HEALTH_ORDER_OUTCOMES.MISCONFIGURED_PIXEL]: 'error',
  [BRAND_HEALTH_ORDER_OUTCOMES.INSERT_FAILED]: 'error',
  [BRAND_HEALTH_ORDER_OUTCOMES.HANDLER_ERROR]: 'error',
  [BRAND_HEALTH_ORDER_OUTCOMES.UNRESOLVED_DOMAIN]: 'error',
  [BRAND_HEALTH_ORDER_OUTCOMES.NO_ATTRIBUTION]: 'neutral',
  // Not a tracking failure: the direct pixel posts every checkout, so one sitewide promo code puts
  // ~100% of a brand's orders here. The Shopify path already grades it neutral.
  [BRAND_HEALTH_ORDER_OUTCOMES.UNREGISTERED_CODE]: 'neutral',
  [BRAND_HEALTH_ORDER_OUTCOMES.DUPLICATE_ORDER]: 'neutral',
  [BRAND_HEALTH_CLICK_OUTCOMES.DUPLICATE_CLICK]: 'neutral',
  [BRAND_HEALTH_ORDER_OUTCOMES.SHOPIFY_HANDOFF]: 'neutral',
  [BRAND_HEALTH_CLICK_OUTCOMES.BOT_BLOCKED]: 'neutral',
  [BRAND_HEALTH_ORDER_OUTCOMES.BLACKLISTED_CODE]: 'neutral',
  [BRAND_HEALTH_ORDER_OUTCOMES.CLICK_NOT_FOUND]: 'neutral',
  [BRAND_HEALTH_ORDER_OUTCOMES.UNREGISTERED_PROPERTY]: 'neutral',
  [BRAND_HEALTH_ORDER_OUTCOMES.ORDER_NOT_FOUND]: 'neutral',
  [BRAND_HEALTH_ORDER_OUTCOMES.ALREADY_CANCELLED]: 'neutral',
  [BRAND_HEALTH_ORDER_OUTCOMES.BANNED_USER]: 'neutral',
  [BRAND_HEALTH_ORDER_OUTCOMES.POS_ORDER]: 'neutral',
  [BRAND_HEALTH_ORDER_OUTCOMES.DRAFT_ORDER]: 'neutral',
  [BRAND_HEALTH_ORDER_OUTCOMES.ZERO_AMOUNT_ORDER]: 'neutral',
  [BRAND_HEALTH_ORDER_OUTCOMES.MULTIPLE_OFFER_CODES]: 'neutral',
  [BRAND_HEALTH_SHOPIFY_API_OUTCOMES.SUCCESS]: 'success',
  [BRAND_HEALTH_SHOPIFY_API_OUTCOMES.AUTH_ERROR]: 'error',
  [BRAND_HEALTH_SHOPIFY_API_OUTCOMES.SESSION_INIT_FAILED]: 'error',
  // We cause the 429s ourselves during syncs, and a Shopify outage is not the brand's credentials.
  [BRAND_HEALTH_SHOPIFY_API_OUTCOMES.RATE_LIMITED]: 'neutral',
  [BRAND_HEALTH_SHOPIFY_API_OUTCOMES.SERVER_ERROR]: 'neutral',
  [BRAND_HEALTH_SHOPIFY_API_OUTCOMES.CLIENT_ERROR]: 'error'
};

// Ours, not the brand's: these keep 'error' severity so the page stays honest, but never enter the
// error-rate numerator (they stay in the denominator), so a bad deploy on our side can't tell
// thousands of brands to take action. handler_error is the clicks family's only error outcome, so
// excluding it makes a brand-facing clicks alert arithmetically impossible.
const BRAND_HEALTH_SHOPMY_SIDE_ERROR_OUTCOMES = [
  BRAND_HEALTH_ORDER_OUTCOMES.HANDLER_ERROR,
  BRAND_HEALTH_ORDER_OUTCOMES.INSERT_FAILED,
  // A malformed query is our bug, not the brand's credentials.
  BRAND_HEALTH_SHOPIFY_API_OUTCOMES.CLIENT_ERROR
];

// How a Shopify store's connectivity is broken or degraded — minted by the probe classifier,
// keyed on by the reconnect guard, the react resolution registry, and the warning chip labels.
const BRAND_HEALTH_SHOPIFY_CONNECTIVITY_ISSUE_TYPES = {
  APP_UNINSTALLED: 'app_uninstalled',
  TOKEN_REVOKED: 'token_revoked',
  CONNECTION_ERROR: 'connection_error',
  MISSING_SCOPES: 'missing_scopes',
  MISSING_WEBHOOKS: 'missing_webhooks',
  API_ERRORS: 'api_errors'
};

// Why an order's click claim was set aside — rides order events as clickDiscardReason, never as
// the outcome: the order itself still lands its own terminal outcome.
const BRAND_HEALTH_CLICK_DISCARD_REASONS = {
  EXPIRED_CLICK: 'expired_click',
  CROSS_BRAND_CLICK: 'cross_brand_click',
  PROPERTY_MISMATCH: 'property_mismatch'
};

// The per-event health indicator names — the aggregate bucket keys node's writer map mints and
// every react read (totals, sections, flat-line activity) joins on byte-for-byte. One home, so a
// rename can't silently zero a counter on the other side.
const BRAND_HEALTH_INDICATOR_KEYS = {
  clickSuccess: 'clickSuccess',
  clickBotBlocked: 'clickBotBlocked',
  clickDuplicate: 'clickDuplicate',
  clickHandlerError: 'clickHandlerError',
  clickIsTest: 'clickIsTest',
  clickConfirmationSuccess: 'clickConfirmationSuccess',
  shopifyCartApiSuccess: 'shopifyCartApiSuccess',
  clickConfirmationIsTest: 'clickConfirmationIsTest',
  orderConfirmationSeen: 'orderConfirmationSeen',
  orderConfirmationOrderAmountIsValid: 'orderConfirmationOrderAmountIsValid',
  orderConfirmationOrderIdIsValid: 'orderConfirmationOrderIdIsValid',
  orderConfirmationCurrencyIsValid: 'orderConfirmationCurrencyIsValid',
  orderConfirmationIsTest: 'orderConfirmationIsTest',
  orderConfirmationTracked: 'orderConfirmationTracked',
  orderConfirmationDiscountCodeIsPresent: 'orderConfirmationDiscountCodeIsPresent',
  orderConfirmationClickIdIsPresent: 'orderConfirmationClickIdIsPresent',
  orderConfirmationIsTrackedViaIP: 'orderConfirmationIsTrackedViaIP',
  orderConfirmationHasReturningCustomer: 'orderConfirmationHasReturningCustomer',
  orderTrackedViaClickId: 'orderTrackedViaClickId',
  orderTrackedViaIpMatch: 'orderTrackedViaIpMatch',
  orderTrackedViaDiscountCode: 'orderTrackedViaDiscountCode',
  orderTrackedViaOffer: 'orderTrackedViaOffer',
  orderTrackedViaUnknown: 'orderTrackedViaUnknown',
  orderUpdateSeen: 'orderUpdateSeen',
  orderUpdateOrderIdIsValid: 'orderUpdateOrderIdIsValid',
  orderUpdateNewOrderAmountIsValid: 'orderUpdateNewOrderAmountIsValid',
  orderUpdateCurrencyIsValid: 'orderUpdateCurrencyIsValid',
  orderUpdateIsTest: 'orderUpdateIsTest',
  orderUpdateSuccess: 'orderUpdateSuccess',
  orderCancellationSeen: 'orderCancellationSeen',
  orderCancellationOrderIdIsValid: 'orderCancellationOrderIdIsValid',
  orderCancellationIsTest: 'orderCancellationIsTest',
  orderCancellationSuccess: 'orderCancellationSuccess',
  shopifyApiCallSuccess: 'shopifyApiCallSuccess',
  shopifyApiCallAuthError: 'shopifyApiCallAuthError'
};

// How an order-tracked event's click was attributed, set once at commission time and carried on the
// event (apiAffiliateCommissions, apiShopify). The Shopify path adds landing_site + note_attributes;
// the direct pixel path cannot see either.
const ORDER_ATTRIBUTION_SOURCES = {
  UNKNOWN: 'unknown',
  IP_MATCH: 'ip_match',
  CLICK_ID: 'click_id',
  LANDING_SITE: 'landing_site',
  NOTE_ATTRIBUTES: 'note_attributes',
  OFFER: 'offer',
  DISCOUNT_CODE: 'discount_code'
};

// Each attribution source folds into exactly one orderTrackedVia* bucket, so the buckets partition
// orderConfirmationTracked (Σ == tracked). click_id, landing_site, and note_attributes are all a
// recovered click, so they share one bucket. A source missing here (unknown, or one added later)
// folds to orderTrackedViaUnknown, so the reconciliation can never silently drop an order.
const ORDER_ATTRIBUTION_SOURCE_TO_INDICATOR = {
  [ORDER_ATTRIBUTION_SOURCES.CLICK_ID]: BRAND_HEALTH_INDICATOR_KEYS.orderTrackedViaClickId,
  [ORDER_ATTRIBUTION_SOURCES.LANDING_SITE]: BRAND_HEALTH_INDICATOR_KEYS.orderTrackedViaClickId,
  [ORDER_ATTRIBUTION_SOURCES.NOTE_ATTRIBUTES]: BRAND_HEALTH_INDICATOR_KEYS.orderTrackedViaClickId,
  [ORDER_ATTRIBUTION_SOURCES.IP_MATCH]: BRAND_HEALTH_INDICATOR_KEYS.orderTrackedViaIpMatch,
  [ORDER_ATTRIBUTION_SOURCES.OFFER]: BRAND_HEALTH_INDICATOR_KEYS.orderTrackedViaOffer,
  [ORDER_ATTRIBUTION_SOURCES.DISCOUNT_CODE]: BRAND_HEALTH_INDICATOR_KEYS.orderTrackedViaDiscountCode
};

// One independent status machine per (Brand_id, domain, family) row in BrandHealthAlertStates.
// New detectors add a family here, never a new column.
const BRAND_HEALTH_ALERT_FAMILIES = {
  CLICK_TRACKING: 'click_tracking',
  SHOPIFY_CONNECTIVITY: 'shopify_connectivity',
  CLICKS: 'clicks',
  ORDERS: 'orders',
  ORDER_UPDATES: 'order_updates',
  ORDER_CANCELLATIONS: 'order_cancellations',
  FLAT_LINE: 'flat_line',
  ATTRIBUTION_DRIFT: 'attribution_drift'
};

// The worst-first status ladder every rollup shares — the detector's brand and domain rollups in
// node and the admin verdict panel. A null status is a stale row (not re-evaluated inside the sweep
// window): it ranks below everything, and a set with no current verdict reports null, never OK.
const BRAND_HEALTH_STATUS_RANK = { BROKEN: 4, WARNING: 3, CHECK: 2, OK: 1 };
const rankBrandHealthStatus = status => BRAND_HEALTH_STATUS_RANK[status] || 0;
const worstBrandHealthStatus = statuses =>
  statuses.reduce((worst, status) => (rankBrandHealthStatus(status) > rankBrandHealthStatus(worst) ? status : worst), null);

// The per-domain tracking verdicts the onboarding wizard and the server-side completion gate both
// read off one health object: { liveData: { [eventType]: lastEvent }, healthData: { allTimeAggregates } }.
// The live event is the most recent one and wins when present; otherwise the all-time counters decide.
const hasLiveEvent = liveEvent => !!liveEvent && Object.keys(liveEvent).length > 0;
const isNonEmptyString = value => typeof value === 'string' && value.length > 0;
const mergeLiveEvents = (liveData, ...eventTypes) => Object.assign({}, ...eventTypes.map(eventType => liveData[eventType] || {}));

// All four capability flags off is the signature the app_uninstalled handler leaves on a BrandIntegrations
// row; a row with any flag still on carries deliberate brand settings and counts as connected.
const shopifyIntegrationIsUninstalled = integration =>
  !integration?.allowOrderCreation && !integration?.allowProductSyncing && !integration?.allowAffiliateTracking && !integration?.allowCustomCodes;

const getDomainTrackingVerdicts = domainHealth => {
  const liveData = domainHealth?.liveData || {};
  const allTime = domainHealth?.healthData?.allTimeAggregates || {};
  const clickConfirmation = liveData[BRAND_HEALTH_EVENT_TYPES.CLICK_CONFIRMATION];
  const order = mergeLiveEvents(liveData, BRAND_HEALTH_EVENT_TYPES.ORDER_SEEN, BRAND_HEALTH_EVENT_TYPES.ORDER_TRACKED);
  const orderUpdate = mergeLiveEvents(liveData, BRAND_HEALTH_EVENT_TYPES.ORDER_UPDATE_SEEN, BRAND_HEALTH_EVENT_TYPES.ORDER_UPDATE_TRACKED);
  const orderCancellation = mergeLiveEvents(liveData, BRAND_HEALTH_EVENT_TYPES.ORDER_CANCEL_SEEN, BRAND_HEALTH_EVENT_TYPES.ORDER_CANCEL_TRACKED);

  // The pixel records failed validations as live events too, with isSuccess false; older events carry no flag.
  const clickConfirmed = hasLiveEvent(clickConfirmation) ? clickConfirmation.isSuccess !== false : allTime.clickConfirmationSuccess > 0;
  const shopifyCartApiSeen = hasLiveEvent(clickConfirmation)
    ? clickConfirmation.isSuccess !== false && !!clickConfirmation.shopifyCartApiSuccess
    : allTime.shopifyCartApiSuccess > 0;
  const orderConfirmed = hasLiveEvent(order)
    ? parseInt(order.orderAmount) > 0 && !!order.order_id && isNonEmptyString(order.currency)
    : allTime.orderConfirmationSeen > 0 &&
      allTime.orderConfirmationOrderAmountIsValid > 0 &&
      allTime.orderConfirmationOrderIdIsValid > 0 &&
      allTime.orderConfirmationCurrencyIsValid > 0;
  const orderUpdatesConfirmed = hasLiveEvent(orderUpdate)
    ? !!orderUpdate.order_id && parseInt(orderUpdate.new_order_amount) > 0 && isNonEmptyString(orderUpdate.currency)
    : allTime.orderUpdateSeen > 0 &&
      allTime.orderUpdateOrderIdIsValid > 0 &&
      allTime.orderUpdateNewOrderAmountIsValid > 0 &&
      allTime.orderUpdateCurrencyIsValid > 0;
  const orderCancellationsConfirmed = hasLiveEvent(orderCancellation)
    ? !!orderCancellation.order_id
    : allTime.orderCancellationSeen > 0 && allTime.orderCancellationOrderIdIsValid > 0;

  return { clickConfirmed, shopifyCartApiSeen, orderConfirmed, orderUpdatesConfirmed, orderCancellationsConfirmed };
};

module.exports = {
  BRAND_HEALTH_ALERT_FAMILIES,
  BRAND_HEALTH_INDICATOR_KEYS,
  ORDER_ATTRIBUTION_SOURCES,
  ORDER_ATTRIBUTION_SOURCE_TO_INDICATOR,
  BRAND_HEALTH_CLICK_DISCARD_REASONS,
  BRAND_HEALTH_SHOPIFY_CONNECTIVITY_ISSUE_TYPES,
  BRAND_HEALTH_EVENT_TYPES,
  BRAND_HEALTH_ROUTES,
  BRAND_HEALTH_CLICK_OUTCOMES,
  BRAND_HEALTH_ORDER_OUTCOMES,
  BRAND_HEALTH_SHOPIFY_API_OUTCOMES,
  BRAND_HEALTH_OUTCOME_KEY_PREFIX,
  BRAND_HEALTH_OUTCOME_SEVERITY,
  BRAND_HEALTH_SHOPMY_SIDE_ERROR_OUTCOMES,
  BRAND_HEALTH_STATUS_RANK,
  rankBrandHealthStatus,
  worstBrandHealthStatus,
  shopifyIntegrationIsUninstalled,
  getDomainTrackingVerdicts
};
